import {
  Component,
  EventEmitter,
  Inject, Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID, SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, shareReplay, take, takeUntil } from 'rxjs/operators';
import { Account } from 'src/app/modules/account/models/account';
import { AccountStoreService } from 'src/app/modules/account/store/account.store.service';
import { Multimedia } from 'src/app/modules/shared/models/multimedia';
import { isPlatformBrowser } from '@angular/common';
import { NotificationService } from '@core/services/notification.service';
import { RequireDate } from '@core/utils/dropdown.option.validator';
import { Guid } from '@core/utils/guid';
import { UserProfile } from '@modules/identity/models/user';
import { IdentityStoreService } from '@modules/identity/store/services/identity-store.service';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';
import { ConfirmEmailValidator } from '@modules/shared/validators/confirm-email.validator';
import { Country } from '@shared/models/country';
import { CountryStoreService } from '@shared/store/country-store/services/country.store.service';
import { InstructorStoreService } from '../../client/store/instructor.store.service';
import { Instructor, IRegion } from '../../models/instructor';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from "@angular/material-moment-adapter";
import { MatDatepickerControl, MatDatepickerPanel } from "@angular/material/datepicker/datepicker-base";
import * as moment from 'moment';
import { LocationStoreService } from "@modules/location/store/services/location-store.service";
import { AllowedCountryList } from "@shared/constants/allowed-countries";
import { PhoneNumberValidators } from "@shared/validators/phone-number.validator";
import { PhoneNumberService } from "@core/services/phone-number.service";
import { isEmpty, isNull, isUndefined } from "lodash";
import { CustomerExperienceApiService } from "@shared/services/customer-experience.api.service";
import { ConfigService } from "@core/services/config.service";
import { InstructorStatusType } from '@modules/shared/enums/instructor-status-type';

@Component({
  selector: 'basic-info-form',
  templateUrl: './basic-info-form.component.html',
  styleUrls: ['./basic-info-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],

    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: MAT_DATE_FORMATS, useValue: {
        parse: {
          dateInput: 'YYYY-MM-DD',
        },
        display: {
          dateInput: 'YYYY-MM-DD',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY',
        },
      }
    },
  ]
})

export class BasicInfoFormComponent implements OnInit, OnDestroy, OnChanges {

  @ViewChild('birthdatePicker', { static: false }) birthDatePicker: MatDatepickerPanel<MatDatepickerControl<Date>, Date | null, Date>;

  @Input() public isTabReadOnly: boolean = false;

  @Input() public instructorStatus: InstructorStatusType = null;

  @Output() formSubmit = new EventEmitter<string>();

  // trigger when form is complete
  @Output() complete = new EventEmitter<string>();

  // trigger when instructor is approve
  @Output() approve = new EventEmitter<string>();

  // trigger when instructor is reject by admin
  @Output() reject = new EventEmitter<string>();

  @Output() reset = new EventEmitter<string>();

  // trigger when instructor request is pending
  @Output() pending = new EventEmitter<string>();

  countryTypehead$ = new Subject<string>();
  country$: Observable<Country[]>;
  countryList$: Observable<Country[]>;
  regions$: Observable<IRegion[]>;

  instructorBasicForm: FormGroup;
  instructorId: string = null;

  instructorStatusType = InstructorStatusType;

  emailSuggestion: string = null;

  // Image File
  profileImage: File;
  badgeImage: File;

  instructorMedia: Multimedia = null;
  badgeMedia: Multimedia = null;

  userInfo: UserProfile;
  account: Account;
  isDraft = true;
  isUpdate = false;
  isUpdateAccount = false;
  adminWebUrl: string = null;

  today: Date = new Date();
  translationScope = TRANSLATION_SCOPE;

  birthDatePickerConfig = {
    min: new Date(new Date().getFullYear() - 120, 1, 1),
    max: new Date(new Date().getFullYear() - 16, 1, 1),
    startAt: new Date(1980, 1, 1),
  }

  private destroy$: Subject<any>;

  constructor(
    @Inject(PLATFORM_ID) private platformId,
    private identityStoreService: IdentityStoreService,
    private accountStoreService: AccountStoreService,
    private countryStoreService: CountryStoreService,
    private instructorStoreService: InstructorStoreService,
    private notificationService: NotificationService,
    private locationStoreService: LocationStoreService,
    private phoneNumberService: PhoneNumberService,
    private customerExperienceApiService: CustomerExperienceApiService,
    private configService: ConfigService
  ) {
    this.createInstructorBasicInfoForm();
    this.destroy$ = new Subject();
    this.adminWebUrl = this.configService.getCurrent().adminWebUrl;
  }

  get isBrowser(): boolean {
    if (isPlatformBrowser(this.platformId)) return true;
    return false;
  }

  ngOnInit() {
    this.countryList$ = this.countryStoreService.getCountriesByIds(AllowedCountryList);
    this.locationStoreService.fetchAllLocation('', 0, 1000);
   if(this.isBrowser) {
     this.patchFormValues();
   }

    this.country$ = this.countryStoreService.getAllCountries();
    this.regions$ = this.locationStoreService.getAllLocations();

    // Set instructor info into form
    this.instructorStoreService
      .getInstructorProfile()
      .pipe(takeUntil(this.destroy$), shareReplay(1))
      .subscribe((instructor) => {
        if (instructor) {
          this.instructorMedia = instructor.medias
            ? instructor.medias.find((x) => x.category === 'profile' && x.default === true)
            : null;
          // Upload Instructor Profile Image
          if (!this.instructorMedia || (this.isUpdate && !!this.profileImage)) {
           if(this.profileImage) {
            this.instructorStoreService.saveDefaultMedia(instructor.id, this.profileImage);
            this.profileImage = null;
           }
          }

          //this.patchInstructorForm(instructor);
          this.instructorId = instructor.id;
          this.isUpdate = true;
          this.isDraft = Boolean(instructor.isDraft) ;
          if (this.instructorMedia && this.profileImage) {
            if (this.isBrowser) {
              window.scroll({
                top: 70,
                left: 100,
                behavior: 'smooth',
              });
            }
            if(!this.isDraft)
              this.formSubmit.next('id-verification');

          }
        } else {
          //this.patchUserInfo();
          this.isUpdate = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.isTabReadOnly && changes.isTabReadOnly.currentValue) {
      this.instructorBasicForm.disable();
    } else {
      this.instructorBasicForm.enable();
    }
  }

  // When component is destroyed
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Create instructor basic info form group
  private createInstructorBasicInfoForm(): void {
    this.instructorBasicForm = new FormGroup({
      userId: new FormControl(''),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      isInvalidEmail: new FormControl(false),
      confirmEmail: new FormControl('', [Validators.required]),
      birthDate: new FormControl('', [RequireDate]),
      phonePrefix: new FormControl('+47', [ Validators.required ]),
      phone: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(12)
      ]),

      organizationNo: new FormControl('', [
        Validators.required,
        Validators.minLength(9),
        Validators.maxLength(9),
        Validators.pattern('^[0-9]*$'),
      ]),
      organizationName: new FormControl('', [Validators.required]),
      address: new FormGroup({
        postalCode: new FormControl('', [
          Validators.required,
          Validators.maxLength(4),
          Validators.minLength(4),
          Validators.pattern('^[0-9]*$'),
        ]),
        city: new FormControl('', [Validators.required]),
        streetAddress: new FormControl('', [Validators.required]),
        region: new FormControl(''),
        country: new FormControl(null, [Validators.required]),
      }),
      description: new FormControl('', [Validators.required]),
      isDraft : new FormControl(true),

      businessMcc: new FormControl('7399'),
      businessUrl: new FormControl('gture.com'),
      havePc: new FormControl(false),
      acceptTnC: new FormControl(false),
      regions: new FormControl('', [Validators.required])
    }, {
      validators: [ ConfirmEmailValidator.validate, PhoneNumberValidators.validatePhoneNumberCountry ]
    });
  }

  mapSelectedRegionId(region: IRegion, selectedRegion: IRegion) {
    return ((region && selectedRegion) ? region.id === selectedRegion.id : region === selectedRegion);
  }

  // After submit the basic info form
  submitInstructorBasicForm(): void {
    if (this.instructorBasicForm.invalid) {
      this.instructorBasicForm.markAllAsTouched();
      return;
    }

    if (!this.instructorMedia && !this.profileImage) {
      if (this.isBrowser) {
        window.scroll({
          top: 70,
          left: 100,
          behavior: 'smooth',
        });
      }
      //this.translate.get('PROFILE-IMG-REQUIRED').subscribe((res: string) => this.notificationService.warning(res));
      return;
    }
    this.instructorBasicForm.get('isDraft').patchValue(false);
    if (this.instructorBasicForm.valid) {
    this.onFormSubmit();
    }
  }

   // After submit the basic info form as a Draft
   submitDraftInstructorBasicForm(): void {
    this.instructorBasicForm.get('isDraft').patchValue(true);
    this.onFormSubmit();
  }

  onFormSubmit(): void {
    const formValue = this.instructorBasicForm.getRawValue();
    const instructor: Instructor = {
      id: this.instructorId ? this.instructorId : Guid.NewGuid(),
      userId: this.userInfo.sub,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      name: this.userInfo.name,
      email: formValue.email,
      address: formValue.address,
      phone: formValue.phonePrefix + formValue.phone,
      birthDate: moment(formValue.birthDate).toISOString(false),
      organizationNo: formValue.organizationNo,
      organizationName: formValue.organizationName,
      havePc: formValue.havePc,
      acceptTnC: formValue.acceptTnC,
      description: formValue.description,
      isDraft: formValue.isDraft,
      businessMcc: '7399',
      businessUrl: 'gture.com',
      regions: formValue.regions
    };
    this.isUpdate ? this.onUpdateSubmit(instructor) : this.registerInstructor(instructor);

    if (this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
  }

  private registerInstructor(instructor: Instructor): void {
    this.instructorStoreService.registerInstructor(instructor);
  }

  // When update the instructor basic info
  onUpdateSubmit(instructor: Instructor): void {
    this.instructorStoreService.updateInstructor(instructor.id, instructor);
  }

  // Mark form as change when profile or badge image upload
  fileUploadChange(): void {
    this.instructorBasicForm.markAsDirty();
  }

  // Check form validation
  isInvalidateControl(controlName: string, validationType: string): boolean {
    return (
      this.instructorBasicForm.get(controlName).hasError(validationType) &&
      (this.instructorBasicForm.get(controlName).dirty || this.instructorBasicForm.get(controlName).touched)
    );
  }


  // Fetch loggedin user info
  private patchUserInfo(userProfile): void {
    const birthDate = userProfile.birthdate ? new Date(userProfile.birthdate).toISOString() : '';

    this.phoneNumberService.splitPhoneNumberAndCountryCode(userProfile.phone_number)
      .pipe(takeUntil(this.destroy$))
      .subscribe((phone) => {
      this.instructorBasicForm.patchValue({
        userId: userProfile.sub,
        firstName: userProfile.given_name,
        lastName: userProfile.family_name,
        name: userProfile.name,
        phonePrefix: phone.phonePrefix ? phone.phonePrefix : '',
        phone: phone.phoneNumber ? phone.phoneNumber : '',
        email: userProfile.email,
        confirmEmail: userProfile.email || '',
        birthDate: birthDate
      });
    });


  }

  // Set instructor info into instructor form group
  private patchInstructorForm(instructor: Instructor): void {

    this.phoneNumberService.splitPhoneNumberAndCountryCode(instructor.phone)
      .pipe(takeUntil(this.destroy$))
      .subscribe((phone) => {
        this.instructorBasicForm.patchValue(
          {
            userId: instructor.userId,
            firstName: instructor.firstName,
            lastName: instructor.lastName,
            email: instructor.email,
            confirmEmail: instructor.email,
            phonePrefix: phone.phonePrefix ? phone.phonePrefix : '',
            phone: phone.phoneNumber ? phone.phoneNumber : '',
            birthDate: instructor.birthDate,
            organizationNo: instructor.organizationNo,
            organizationName: instructor.organizationName,
            havePc: instructor.havePc,
            acceptTnC: instructor.acceptTnC,
            isDraft : instructor.isDraft,
            address: {
              postalCode: instructor.address.postalCode,
              city: instructor.address.city,
              streetAddress: instructor.address.streetAddress,
              region: instructor.address.region,
              country: instructor.address.country,
            },
            regions: instructor.regions,
            description: instructor.description,
          },
          { onlySelf: true }
        );
      });
    this.instructorBasicForm.updateValueAndValidity();
  }


  private patchFormValues(): void {
    combineLatest(
      [
        this.identityStoreService.getUserProfile(),
        this.instructorStoreService.getInstructorProfile()])
      .pipe(
        filter(([user, instructor]) => !(isEmpty(user) || isNull(user) || isUndefined(user)))
      ).subscribe(([user, instructor]) => {
      this.userInfo = user;
      if ((isEmpty(instructor) || isNull(instructor) || isUndefined(instructor)) && !!user) {
        this.patchUserInfo(user);
      } else if (!isEmpty(instructor)) {
        this.patchInstructorForm(instructor);
      }
    });
  }

  openBirthdatePicker(): void {
    this.birthDatePicker?.open();
  }

  validateEmail() {
    const emailFormControl = this.instructorBasicForm.get('email');

    if (emailFormControl.status === 'VALID') {
      this.customerExperienceApiService
        .getEmailValidation(emailFormControl.value, 'instructor application form')
        .pipe(
          filter(response => !isEmpty(response) && !isNull(response) && !isUndefined(response)),
          take(1)
        ).subscribe(
        (response) => {
          this.instructorBasicForm.get('isInvalidEmail').patchValue(!response.isValid);
          this.emailSuggestion = response.suggestion;
        });
    }
  }
}
